import { mat4, type ReadonlyMat4 } from 'gl-matrix';
import { fetchWithRetry } from '~/backend/network-retry';
import type { Image3D } from '~/domain/patient/image3d';
import type { Grid } from '~/engine/data/common/Grid';
import type { VoiBounds } from '~/state/atoms/toolsAtoms';
import type { EngineReply, EngineRequest } from './registrationEngine.worker';

/** Numbers the engine's `register` reads (registration-engine/src/abi.rs). */
const PARAMS = 62;

/** Why the engine's `register` gave no result, by its return code. */
const FAILURES: Record<number, string> = {
    1: 'the image geometry or registration parameters are invalid',
    2: "an image's voxels do not match its dimensions",
    3: 'the starting alignment is not an invertible transform',
    4: 'the registration options are invalid',
    5: 'the images do not overlap enough to register',
    6: 'an image has a single intensity in the registered region',
    7: 'no placement of the images overlaps their anatomy enough to align them',
};

/** A registration the engine ran and refused, with the reason in words. */
export class RegistrationFailure extends Error {
    public constructor(public readonly reason: string) {
        super(`Registration failed: ${reason}.`);
        this.name = 'RegistrationFailure';
    }
}

export interface EngineRegistration {
    /** Maps secondary (moving) patient space to primary (fixed) patient space. */
    matrix: mat4;
    /** Anatomy agreement of a global alignment, 0 to 1; null without one. */
    score: number | null;
    /** Best agreement of a distinct global alignment; null when there is none. */
    runnerUp: number | null;
    /** Mutual information of the two images at the fit, at the finest level. */
    mutualInformation: number;
}

/** Matrix from a voxel index, taken as the voxel's centre, to patient space. */
export function voxelCentreToPatient(grid: Grid): mat4 {
    return mat4.translate(mat4.create(), grid.voxelToWorld, [0.5, 0.5, 0.5]);
}

/** The engine's `register` parameters for registering `moving` to `fixed`. */
export function engineParams(
    fixed: Grid,
    moving: Grid,
    start: ReadonlyMat4,
    voi: VoiBounds | null,
    global: boolean
): Float64Array {
    const params = new Float64Array(PARAMS);
    params.set(fixed.getSize(), 0);
    params.set(voxelCentreToPatient(fixed), 3);
    params.set(moving.getSize(), 19);
    params.set(voxelCentreToPatient(moving), 22);
    params.set(start, 38);
    params[54] = global ? 1 : 0;
    if (voi) {
        params[55] = 1;
        params.set(
            [voi.minX, voi.minY, voi.minZ, voi.maxX, voi.maxY, voi.maxZ],
            56
        );
    }
    return params;
}

let engineModule: Promise<WebAssembly.Module> | null = null;

/** The engine's compiled module, fetched once; a failed load is retried on the next call. */
function loadEngine(): Promise<WebAssembly.Module> {
    engineModule ??= (async () => {
        const url = new URL(
            `${import.meta.env.BASE_URL || '/'}registration-engine.wasm`,
            window.location.href
        ).href;
        // Revalidated on every load: the URL is fixed, and JS of one release
        // must never run a cached module of another.
        const response = await fetchWithRetry(
            async () => ({ url, init: { cache: 'no-cache' } }),
            { label: 'GET registration-engine.wasm' }
        );
        if (!response.ok) {
            throw new Error(
                `The registration engine could not be loaded (HTTP ${response.status}).`
            );
        }
        const bytes = await response.arrayBuffer();
        // A server without the module may answer with its index page.
        if (!WebAssembly.validate(bytes)) {
            throw new Error(
                'The registration engine is not a WebAssembly module; build ' +
                    'it with `yarn build:registration-engine`.'
            );
        }
        return WebAssembly.compile(bytes);
    })().catch((error: unknown) => {
        engineModule = null;
        throw error;
    });
    return engineModule;
}

/**
 * Registers `secondary` to `primary` in a worker running the registration
 * engine: from `start` (secondary to primary), or, when `global`, from an
 * alignment searched with no prior alignment. The fit uses the primary's
 * voxels inside `voi` when given. Aborting `signal` stops the worker and
 * rejects with an `AbortError`.
 */
export async function registerRigid(
    primary: Image3D,
    secondary: Image3D,
    start: ReadonlyMat4,
    voi: VoiBounds | null,
    global: boolean,
    signal?: AbortSignal
): Promise<EngineRegistration> {
    const module = await loadEngine();
    signal?.throwIfAborted();
    const request: EngineRequest = {
        module,
        params: engineParams(primary.grid, secondary.grid, start, voi, global),
        // Copied, not transferred: the images keep their voxels.
        fixed: primary.pixelData,
        moving: secondary.pixelData,
    };
    const worker = new Worker(
        new URL('./registrationEngine.worker.ts', import.meta.url),
        { type: 'module' }
    );
    let onAbort = () => {};
    try {
        const reply = await new Promise<EngineReply>((resolve, reject) => {
            onAbort = () => reject(signal?.reason);
            signal?.addEventListener('abort', onAbort);
            worker.onmessage = (event: MessageEvent<EngineReply>) =>
                resolve(event.data);
            worker.onerror = (event) =>
                reject(
                    new Error(
                        `The registration engine stopped: ${event.message}`
                    )
                );
            worker.onmessageerror = () =>
                reject(
                    new Error(
                        'The registration engine sent an unreadable reply.'
                    )
                );
            worker.postMessage(request);
        });
        if ('error' in reply) {
            throw new Error(`The registration engine failed: ${reply.error}`);
        }
        if (reply.status !== 0) {
            throw new RegistrationFailure(
                FAILURES[reply.status] ?? `engine status ${reply.status}`
            );
        }
        const optional = (value: number) =>
            Number.isNaN(value) ? null : value;
        const matrix = mat4.create() as Float32Array;
        matrix.set(reply.result.subarray(0, 16));
        return {
            matrix,
            score: optional(reply.result[16]),
            runnerUp: optional(reply.result[17]),
            mutualInformation: reply.result[18],
        };
    } finally {
        signal?.removeEventListener('abort', onAbort);
        worker.terminate();
    }
}
